/**
 * Watering ordinance evaluation for the Lawn Irrigation Dashboard.
 *
 * Turns an `Ordinance` record plus a date into a plain answer: may this property
 * water right now, and why. Kept separate from the widget so the same rule logic
 * can be reused by a scheduler later.
 */

const WEEKDAY_NAMES = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
];

/** "HH:MM" -> minutes since midnight, or null when unset/malformed. */
const toMinutes = (value) => {
  if (typeof value !== 'string') return null;
  const match = value.match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours > 23 || minutes > 59) return null;
  return hours * 60 + minutes;
};

export const formatTimeLabel = (value) => {
  const minutes = toMinutes(value);
  if (minutes === null) return null;
  const hour24 = Math.floor(minutes / 60);
  const minute = minutes % 60;
  const suffix = hour24 >= 12 ? 'PM' : 'AM';
  const hour12 = hour24 % 12 === 0 ? 12 : hour24 % 12;
  return `${hour12}:${String(minute).padStart(2, '0')} ${suffix}`;
};

/** Address house number, used by odd/even rules. */
const houseNumberFrom = (address) => {
  const match = (address || '').match(/\d+/);
  return match ? Number(match[0]) : null;
};

const dayMatches = (allowedDays, date) => {
  if (!allowedDays || allowedDays.length === 0) return true;
  const weekday = WEEKDAY_NAMES[date.getDay()];
  return allowedDays.some((day) => String(day).toLowerCase() === weekday.toLowerCase());
};

const withinWindow = (ordinance, date) => {
  const start = toMinutes(ordinance.allowedTimeStart);
  const end = toMinutes(ordinance.allowedTimeEnd);
  if (start === null || end === null) {
    return { applies: false, inWindow: true };
  }

  const nowMinutes = date.getHours() * 60 + date.getMinutes();
  // A window that wraps midnight (e.g. 19:00-06:00) is treated as two ranges.
  const inWindow =
    start <= end
      ? nowMinutes >= start && nowMinutes <= end
      : nowMinutes >= start || nowMinutes <= end;

  return { applies: true, inWindow };
};

/**
 * Human-readable summary of the rule itself, independent of the current time.
 *
 * @param {import('../types/lawnTypes').Ordinance} ordinance
 * @returns {string}
 */
export function describeRule(ordinance) {
  if (!ordinance) return 'No ordinance on file.';

  const window =
    formatTimeLabel(ordinance.allowedTimeStart) && formatTimeLabel(ordinance.allowedTimeEnd)
      ? ` between ${formatTimeLabel(ordinance.allowedTimeStart)} and ${formatTimeLabel(
          ordinance.allowedTimeEnd
        )}`
      : '';

  switch (ordinance.ruleType) {
    case 'odd_even': {
      const parity = ordinance.allowedDays?.[0] === 'even' ? 'even' : 'odd';
      return `Watering allowed on ${parity} calendar days${window}.`;
    }
    case 'day_of_week': {
      const days = (ordinance.allowedDays || []).join(', ');
      return days
        ? `Watering allowed on ${days}${window}.`
        : `Watering allowed any day${window}.`;
    }
    case 'time_window':
      return window
        ? `Watering allowed daily${window}.`
        : 'Watering allowed at any time.';
    default:
      return 'Ordinance rule type not recognized.';
  }
}

/**
 * Decide whether watering is allowed right now.
 *
 * @param {import('../types/lawnTypes').Ordinance} ordinance
 * @param {Object} [options]
 * @param {Date} [options.now] - evaluation time, defaults to the current time
 * @param {string} [options.address] - property address, for odd/even rules
 * @returns {{allowed: (boolean|null), headline: string, reason: string, ruleText: string}}
 */
export function evaluateOrdinance(ordinance, { now = new Date(), address = '' } = {}) {
  if (!ordinance) {
    return {
      allowed: null,
      headline: 'No ordinance on file',
      reason: 'Add a municipality rule to see whether watering is allowed today.',
      ruleText: '',
    };
  }

  const ruleText = describeRule(ordinance);
  const dayOfMonth = now.getDate();
  const timing = withinWindow(ordinance, now);

  if (ordinance.ruleType === 'odd_even') {
    const parity = ordinance.allowedDays?.[0] === 'even' ? 'even' : 'odd';
    const dayIsOdd = dayOfMonth % 2 === 1;
    const dayQualifies = parity === 'odd' ? dayIsOdd : !dayIsOdd;
    const houseNumber = houseNumberFrom(address);

    // Odd/even ordinances usually key off the address, not just the date.
    const addressQualifies =
      houseNumber === null
        ? true
        : parity === 'odd'
        ? houseNumber % 2 === 1
        : houseNumber % 2 === 0;

    if (!dayQualifies) {
      return {
        allowed: false,
        headline: 'Not allowed today',
        reason: `Today is day ${dayOfMonth}, and this ordinance allows ${parity} calendar days.`,
        ruleText,
      };
    }
    if (!addressQualifies) {
      return {
        allowed: false,
        headline: 'Not allowed for this address',
        reason: `House number ${houseNumber} does not match the ${parity} address group.`,
        ruleText,
      };
    }
    if (timing.applies && !timing.inWindow) {
      return {
        allowed: false,
        headline: 'Outside the allowed hours',
        reason: `Today qualifies, but watering is limited to ${formatTimeLabel(
          ordinance.allowedTimeStart
        )}–${formatTimeLabel(ordinance.allowedTimeEnd)}.`,
        ruleText,
      };
    }
    return {
      allowed: true,
      headline: 'Watering allowed now',
      reason: `Day ${dayOfMonth} matches the ${parity}-day rule${
        timing.applies ? ' and the current time is inside the allowed window' : ''
      }.`,
      ruleText,
    };
  }

  if (ordinance.ruleType === 'day_of_week') {
    if (!dayMatches(ordinance.allowedDays, now)) {
      return {
        allowed: false,
        headline: 'Not allowed today',
        reason: `${WEEKDAY_NAMES[now.getDay()]} is not in the allowed days list.`,
        ruleText,
      };
    }
    if (timing.applies && !timing.inWindow) {
      return {
        allowed: false,
        headline: 'Outside the allowed hours',
        reason: `${WEEKDAY_NAMES[now.getDay()]} qualifies, but not at this hour.`,
        ruleText,
      };
    }
    return {
      allowed: true,
      headline: 'Watering allowed now',
      reason: `${WEEKDAY_NAMES[now.getDay()]} is an allowed watering day.`,
      ruleText,
    };
  }

  // time_window and any unrecognized rule type fall back to the hour check.
  if (timing.applies && !timing.inWindow) {
    return {
      allowed: false,
      headline: 'Outside the allowed hours',
      reason: `Watering is limited to ${formatTimeLabel(
        ordinance.allowedTimeStart
      )}–${formatTimeLabel(ordinance.allowedTimeEnd)}.`,
      ruleText,
    };
  }

  return {
    allowed: true,
    headline: 'Watering allowed now',
    reason: 'The current time is inside the allowed watering window.',
    ruleText,
  };
}
